'use client'

import {
  DotsThreeOutlineIcon,
  DownloadSimpleIcon,
  FacebookLogoIcon,
  LinkIcon,
  WhatsappLogoIcon,
  XLogoIcon
} from '@phosphor-icons/react'
import { Ring2 } from 'ldrs/react'
import { useTranslations } from 'next-intl'
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton } from 'react-share'
import { toast } from 'sonner'

import { Copy } from '@/components/common/copy'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerTitle,
  DrawerDescription
} from '@/components/ui/drawer'
import { useShare, useVideoDownload } from '@/hooks/utils'

import { useWorkContext } from '../work-context'

export const ShareDrawer = ({ children }: { children: React.ReactNode }) => {
  const t = useTranslations()

  const { data } = useWorkContext()

  const [isDownloading, downloadVideo] = useVideoDownload()

  const { isSupported, share } = useShare()

  const handleShare = () => {
    if (!isSupported) {
      toast.error(t('tips.shareNotSupported'))
      return
    }
    share({
      url: data?.videoUrl
    })
  }

  if (!data) {
    return null
  }

  return (
    <Drawer>
      <DrawerTrigger asChild>{children}</DrawerTrigger>
      <DrawerContent className="z-modal">
        <DrawerTitle className="sr-only">Share</DrawerTitle>
        <DrawerDescription className="sr-only">Share the work</DrawerDescription>
        <div className="grid grid-cols-4 gap-x-6 gap-y-4 px-4 pb-5">
          <div className="flex flex-col items-center gap-1">
            <Copy asChild text={location.href} type="link">
              <Button className="size-12 rounded-full bg-light-600" size="icon">
                <LinkIcon className="size-5" />
              </Button>
            </Copy>
            <span className="truncate text-center text-xs font-medium">{t('work.copyLink')}</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <TwitterShareButton
              className="flex size-12 items-center justify-center rounded-full bg-primary"
              resetButtonStyle={false}
              url={data.videoUrl}
            >
              <XLogoIcon className="size-5" />
            </TwitterShareButton>
            <span className="truncate text-center text-xs font-medium">X(Twitter)</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <WhatsappShareButton
              className="flex size-12 items-center justify-center rounded-full bg-brand-whatsapp"
              resetButtonStyle={false}
              url={data.videoUrl}
            >
              <WhatsappLogoIcon className="size-5" />
            </WhatsappShareButton>
            <span className="truncate text-center text-xs font-medium">WhatsApp</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <FacebookShareButton
              className="flex size-12 items-center justify-center rounded-full bg-brand-facebook"
              resetButtonStyle={false}
              url={data.videoUrl}
            >
              <FacebookLogoIcon className="size-5" />
            </FacebookShareButton>
            <span className="truncate text-center text-xs font-medium">Facebook</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <Button
              className="flex size-12 items-center justify-center rounded-full bg-light-600"
              disabled={isDownloading}
              size="icon"
              onClick={() => downloadVideo(data.videoUrl)}
            >
              {isDownloading ? (
                <Ring2 color="white" size={20} stroke={2} />
              ) : (
                <DownloadSimpleIcon className="size-5" />
              )}
            </Button>
            <span className="truncate text-center text-xs font-medium">{t('common.download')}</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <Button
              className="flex size-12 items-center justify-center rounded-full bg-light-600"
              size="icon"
              onClick={handleShare}
            >
              <DotsThreeOutlineIcon className="size-5" />
            </Button>
            <span className="truncate text-center text-xs font-medium">{t('common.more')}</span>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
